import type React from "react";
import { match } from "ts-pattern";
import { z } from "zod";

export type I18NFunction<T = string> = (en: T, fr: T) => T;
// export type I18NComponentFunction<T = React.ReactNode> = (en: T, fr: T) => T;

type ConditionalConfig = [
  // path
  string,
  // operator
  "equals" | "not-equals" | "contains-case-insensitive" | "not-contains-case-insensitive",
  // value
  string | boolean | string[] | number | number[] | null | undefined,
];

type Dependencies = {
  if: ConditionalConfig;
  or?: ConditionalConfig;
  or2?: ConditionalConfig;
  and?: ConditionalConfig;
  and2?: ConditionalConfig;
  then: "hide" | "require";
};

//
// All form field configs must inherit from this interface
//
interface BaseFormFieldConfig {
  label: React.ReactNode | string;
  stringSafeFallback?: string;
  description?: string;
  helpText?: string;
  formItemClassName?: string;
  // dependencies?: [string, string, string | boolean | string[] | number | number[] | null | undefined];
  // hideIf?: ConditionalConfig;
  // requireIf?: ConditionalConfig;
  // requireIf?: {
  //   field: string;
  //   operator: "equals" | "not-equals" | "contains-case-insensitive" | "not-contains-case-insensitive";
  //   value: string | boolean | string[] | number | number[] | null | undefined;
  // }[];
  dependencies?: Dependencies[];
  hideNotApplicableCheckbox?: boolean;
}

export type ZodParent<T extends z.ZodTypeAny> =
  | T
  | z.ZodOptional<T>
  | z.ZodEffects<T, any, any>
  | z.ZodNullable<T>
  | z.ZodOptional<z.ZodNullable<T>>
  // | z.ZodUnion<readonly [T, ...z.ZodTypeAny[]]>;
  | z.ZodUnion<readonly [T, z.ZodLiteral<"not_applicable">]>;

//
// type: Input
//
type TextConfig = BaseFormFieldConfig & {
  type: "text";
  schema: ZodParent<z.ZodString>;
  placeholder?: string;
  defaultValue?: string;
};

//
// type: Textarea
//
type TextareaConfig = BaseFormFieldConfig & {
  type: "textarea";
  schema: ZodParent<z.ZodString>; //| ((l: (en: string, fr: string) => string) => ZodOptionalOrEffects<z.ZodString>);
  placeholder?: string;
  defaultValue?: string;
  offerNotApplicable?: boolean;
};

//
// type: Radio Group
//
type RadioGroupConfig = BaseFormFieldConfig & {
  type: "radioGroup";
  schema: ZodParent<z.ZodEnum<any>>; //| ((l: (en: string, fr: string) => string) => ZodOptionalOrEffects<z.ZodEnum<any>>);
  placeholder?: string;
  mapToLabels?: (key: string, l: I18NFunction) => string;
  defaultValue?: string;
  cols?: number;
};

//
// type: Multi Select
//
type CheckboxGroupConfig = BaseFormFieldConfig & {
  type: "checkboxGroup";
  schema: ZodParent<z.ZodArray<z.ZodEnum<any>>>; //| ((l: (en: string, fr: string) => string) => ZodOptionalOrEffects<z.ZodArray<z.ZodEnum<any>>>);
  placeholder?: string;
  mapToLabels?: (key: string, l: I18NFunction) => string;
  defaultValue?: string[];
  cols?: number;
};

//
// type: Not Applicable / Single Select
//
type SingleCheckboxConfig = BaseFormFieldConfig & {
  type: "singleCheckbox";
  schema: ZodParent<z.ZodBoolean>;
  defaultValue?: boolean;
};

//
// type: Switch
//
type SwitchConfig = BaseFormFieldConfig & {
  type: "switch";
  schema: ZodParent<z.ZodBoolean>; //| ((l: (en: string, fr: string) => string) => ZodOptionalOrEffects<z.ZodBoolean>);
  defaultValue?: boolean;
};

//
// type: Hidden
//
type HiddenConfig = BaseFormFieldConfig & {
  type: "hidden";
  schema: ZodParent<z.ZodString>;
};

//
// type: Markdown
//
type MarkdownConfig = BaseFormFieldConfig & {
  type: "markdown";
  schema?: ZodParent<z.ZodString>; // Optional, doesn't collect data
};

//
// type: Ranking
//
type RankingConfig = BaseFormFieldConfig & {
  type: "ranking";
  schema: ZodParent<z.ZodArray<z.ZodString>>;
  items: { key: string; label: string; optional?: boolean }[];
  defaultValue?: string[];
};

/**
 * The stuff that can be used inside an object. */
export type ObjectConfigValues =
  | TextConfig
  | TextareaConfig
  | RadioGroupConfig
  | CheckboxGroupConfig
  | SwitchConfig
  | SingleCheckboxConfig
  | HiddenConfig
  | MarkdownConfig
  | RankingConfig;

export type ObjectConfig = BaseFormFieldConfig & {
  type: "object";
  properties: Record<string, ObjectConfigValues>;
  offerNotApplicableForEntireObject?: boolean;
};

export type FormConfig<K extends Record<string, any> = Record<string, any>> = Record<
  keyof K,
  ObjectConfigValues | ObjectConfig
>;
// export type FormConfig<K extends Record<string, any> = Record<string, any>> = {
//   [P in keyof K]?: ObjectConfigValues | ObjectConfig;
// };

export type FormConfigInPages = {
  pageTitle: string;
  pageDescription: string;
  formFields: FormConfig;
}[];

export type SectionConfig = {
  sectionId:
    | "initial-presentation"
    | "confirmatory-diagnosis"
    | "treatment-early-stage"
    | "treatment-late-stage"
    | "ongoing-management";
  sectionNumber: number;
  title: string;
  description: string;
  diagrams: {
    // src: string | ((l: (en: string, fr: string) => string) => string);
    // alt: string | ((l: (en: string, fr: string) => string) => string);
    src: string;
    alt: string;
  }[];
  formPages: {
    formConfig: FormConfig;
    formSchemas: {
      default: z.ZodEffects<z.ZodObject<any>>;
      partial: z.ZodEffects<z.ZodObject<any>>;
    };
    defaultValues: Record<string, any>;
  }[];
  // formKeys: FormKeys<FormConfig>[];
  /**
   * The root keys are the IDs used in the DB.
   */
  // formRootKeys: string[];
  /**
   * A map of response IDs to form configs.
   * I.e. { "isTheUseOfTheseGuidelinesConsistentAcrossTheRegion": { "isConsistent": { "type": "boolean" ... } } }
   */
  responseKeyToFormConfig: Record<string, ObjectConfigValues | ObjectConfig>;
  // formSchema: z.ZodSchema;
};

// create a type "FormKeys" that is the root keys of the form config AND the keys of "properties" in the "object" type
export type FormKeys<T extends FormConfig> =
  | keyof T
  | {
      [K in keyof T]: T[K] extends { type: "object"; properties: infer P }
        ? `${K & string}.${keyof P & string}`
        : never;
    }[keyof T];

// a function that takes a form config and returns the keys in dot notation
// it's the same as the FormKeys type, but it's a function
// that is the root keys of the form config AND the keys of "properties" in the "object" type
// i.e. return ["pointOfInvolvement", "delaysInPresentationAndEvaluation.hasDelays", "delaysInPresentationAndEvaluation.top3Causes", "howDoYouTriageAndDetermineSpecialty", "whichGuidelinesDoYouFollowForTriage", "isTheUseOfTheseGuidelinesConsistentAcrossTheRegion.isConsistent", "isTheUseOfTheseGuidelinesConsistentAcrossTheRegion.disparities"]
export const getFormKeys = <T extends FormConfig>(formConfig: T) => {
  const rootKeys = Object.keys(formConfig);
  const objectKeys = rootKeys.filter((key) => formConfig[key as keyof T]?.type === "object");
  const objectKeysInDotNotation = objectKeys.map((key) => {
    const object = formConfig[key as keyof T] as ObjectConfig;
    return Object.keys(object.properties).map((property) => `${key}.${property}`);
  });

  return [...rootKeys, ...objectKeysInDotNotation.flat()].filter((k) => {
    // don't include the keys that are in the object keys in dot notation
    if (objectKeys.includes(k)) return !rootKeys.includes(k);

    return true;
  });
};

/**
 * Returns true if the dependency value passes the operator check against the value to check against.
 */
export function testFieldAgainstDependencyOperator({
  operator,
  valueToCheckAgainst,
  dependencyValue,
}: {
  operator: ConditionalConfig[1];
  valueToCheckAgainst: ConditionalConfig[2];
  dependencyValue: ConditionalConfig[2];
}) {
  return !!match(operator)
    .with("equals", () => {
      if (dependencyValue === valueToCheckAgainst) {
        return true;
      }
    })
    .with("not-equals", () => {
      if (dependencyValue !== valueToCheckAgainst) {
        return true;
      }
    })
    .with("contains-case-insensitive", () => {
      // check if the dependency value is an array and if it contains the value to check against
      if (
        Array.isArray(dependencyValue) &&
        dependencyValue.some((item) => String(item).toLowerCase().includes(String(valueToCheckAgainst).toLowerCase()))
      ) {
        return true;
      }

      // cast as a string
      if (String(dependencyValue).toLowerCase().includes(String(valueToCheckAgainst).toLowerCase())) {
        return true;
      }
    })
    .with("not-contains-case-insensitive", () => {
      // check if the dependency value is an array and if it DOES NOT contain the value to check against
      if (
        Array.isArray(dependencyValue) &&
        !dependencyValue.some((item) => String(item).toLowerCase().includes(String(valueToCheckAgainst).toLowerCase()))
      ) {
        return true;
      }

      // cast as a string
      if (!String(dependencyValue).toLowerCase().includes(String(valueToCheckAgainst).toLowerCase())) {
        return true;
      }
    })
    .exhaustive();
}

export function testFieldAgainstAllDependencies({
  testFor,
  dependencies,
  getDependencyValue,
}: {
  testFor: Dependencies["then"];
  dependencies: Dependencies[];
  getDependencyValue: (pathToDependency: string) => any;
}) {
  let doesFieldPassAllDependencies = false;

  //
  // only look at "hide" dependencies
  //
  dependencies
    .filter((dependency) => dependency.then === testFor)
    .forEach((dependency) => {
      // const [pathToDependency, operator, valueToCheckAgainst] = dependency.if;

      //
      // check the "if" dependency
      //
      const if_dependencyValue = getDependencyValue(dependency.if[0]);
      const doesFieldPass_if_check = testFieldAgainstDependencyOperator({
        operator: dependency.if[1],
        valueToCheckAgainst: dependency.if[2],
        dependencyValue: if_dependencyValue,
      });

      if (doesFieldPass_if_check) {
        doesFieldPassAllDependencies = true;
      }

      //
      // check the "or" dependency
      //
      if (dependency.or) {
        const or_dependencyValue = getDependencyValue(dependency.or[0]);
        const doesFieldPass_or_check = testFieldAgainstDependencyOperator({
          operator: dependency.or[1],
          valueToCheckAgainst: dependency.or[2],
          dependencyValue: or_dependencyValue,
        });

        if (doesFieldPass_or_check) {
          doesFieldPassAllDependencies = true;
        }
      }

      //
      // check the "or2" dependency
      //
      if (dependency.or2) {
        const or2_dependencyValue = getDependencyValue(dependency.or2[0]);
        const doesFieldPass_or2_check = testFieldAgainstDependencyOperator({
          operator: dependency.or2[1],
          valueToCheckAgainst: dependency.or2[2],
          dependencyValue: or2_dependencyValue,
        });

        if (doesFieldPass_or2_check) {
          doesFieldPassAllDependencies = true;
        }
      }

      //
      // check the "and" dependency
      //
      if (dependency.and) {
        const and_dependencyValue = getDependencyValue(dependency.and[0]);
        const doesFieldPass_and_check = testFieldAgainstDependencyOperator({
          operator: dependency.and[1],
          valueToCheckAgainst: dependency.and[2],
          dependencyValue: and_dependencyValue,
        });

        // "and" dependencies are ANDed together
        doesFieldPassAllDependencies = doesFieldPassAllDependencies && doesFieldPass_and_check;
      }

      //
      // check the "and2" dependency
      //
      if (dependency.and2) {
        const and2_dependencyValue = getDependencyValue(dependency.and2[0]);
        const doesFieldPass_and2_check = testFieldAgainstDependencyOperator({
          operator: dependency.and2[1],
          valueToCheckAgainst: dependency.and2[2],
          dependencyValue: and2_dependencyValue,
        });

        // "and" dependencies are ANDed together
        doesFieldPassAllDependencies = doesFieldPassAllDependencies && doesFieldPass_and2_check;
      }
    });

  return doesFieldPassAllDependencies;
}
